import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type FormEvent, type MouseEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  createQuickDiagnosticAction,
  quickDiagnosticActionQuery,
  quickDiagnosticActionsQuery,
  quickDiagnosticActionsTableQuery,
  removeQuickDiagnosticAction,
  updateQuickDiagnosticAction,
  type ActionFields,
  type DiagnosticScope,
} from '../../api/settingsDiagnosticActions'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Easeedesk > AI Analizer Actions > Quick Diagnostic Actions, element cu
 * element fata de `diagnostic/settings/actions.php` + `QuickDiagnostic()`
 * din `adam_intop_settings.js`: tabelul DataTables (Id, Scope, Condition,
 * Value, Command, Edit/Delete), fereastra "New action" / "Update action" cu
 * scope-ul si, dupa tipul lui, conditia si valoarea (`scope_conditions.php`,
 * aici `ScopeConditions`), si comanda.
 */
const TABLE_ID = 'quick-diag-suggestions-table'
const ORDER_NAMES = ['id', 'scope', 'scope_condition', 'condition_value', 'command_name', 'id']
const EMPTY: ActionFields = { scope_id: '', scope_condition: '=', condition_value: '', command_id: '' }

function esc(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

function columns(): TableColumn[] {
  const text = (key: string): TableColumn['html'] => (row) => esc(String(row[key] ?? ''))
  return [
    { index: 0, title: 'Id', orderable: true, visible: true, className: 'align-left', html: text('id') },
    { index: 1, title: 'Scope', orderable: true, visible: true, className: 'align-left', html: text('scope') },
    { index: 2, title: 'Condition', orderable: false, visible: true, className: 'align-left', html: text('scope_condition') },
    { index: 3, title: 'Value', orderable: true, visible: true, className: 'align-left', html: text('condition_value') },
    { index: 4, title: 'Command', orderable: true, visible: true, className: 'align-left', html: text('command_name') },
    {
      index: 5,
      title: 'Actions',
      orderable: false,
      visible: true,
      width: 200,
      html: (row) =>
        `<button data-id="${Number(row.id)}" class="btn btn-sm btn-primary edit-action">Edit</button> ` +
        `<button class="btn btn-sm btn-danger remove-action" data-action="${Number(row.id)}">Delete</button>`,
    },
  ]
}

/** `scope_conditions.php`: operatorii si valorile pe fiecare `scope_type`. */
export function conditionsFor(scopeType: string): { operators: string[]; values: { value: string; label: string }[] | null; suffix?: string } {
  switch (scopeType) {
    case 'percentage':
      return { operators: ['=', '>', '<', '!='], values: null, suffix: '%' }
    case 'numeric':
      return { operators: ['=', '>', '<', '!='], values: null }
    case 'compare_version':
      return { operators: ['=', '>', '<', '!='], values: [{ value: 'LAST_VERSION', label: 'Last version' }] }
    case 'boolean':
      return { operators: ['='], values: [{ value: 'TRUE', label: 'TRUE' }, { value: 'FALSE', label: 'FALSE' }] }
    case 'time':
      return { operators: ['=', '>', '<', '!='], values: [{ value: 'CURRENT_TIME', label: 'Current time' }] }
    case 'resolution':
      return { operators: ['<'], values: [{ value: 'MAX_SUPPORTED', label: 'Max Supported' }] }
    default:
      return { operators: [], values: [] }
  }
}

function ScopeConditions({ scope, form, onChange }: { scope: DiagnosticScope | undefined; form: ActionFields; onChange(next: ActionFields): void }) {
  if (!scope) return null
  const c = conditionsFor(scope.scope_type)
  if (c.operators.length === 0) return null
  return (
    <>
      <div className="form-group scopeCondition">
        <label>Condition</label>
        <select className="form-control" name="scope_condition" value={form.scope_condition} onChange={(e) => onChange({ ...form, scope_condition: e.target.value })}>
          {c.operators.map((op) => (
            <option key={op} value={op}>
              {op}
            </option>
          ))}
        </select>
      </div>
      <div className="form-group conditionValue">
        <label>Value</label>
        {c.values === null ? (
          c.suffix ? (
            <div className="input-group">
              <input type="text" className="form-control" name="condition_value" value={form.condition_value} onChange={(e) => onChange({ ...form, condition_value: e.target.value })} />
              <span className="input-group-addon">{c.suffix}</span>
            </div>
          ) : (
            <input type="text" className="form-control" name="condition_value" value={form.condition_value} onChange={(e) => onChange({ ...form, condition_value: e.target.value })} />
          )
        ) : (
          <select className="form-control" name="condition_value" value={form.condition_value} onChange={(e) => onChange({ ...form, condition_value: e.target.value })}>
            {c.values.map((v) => (
              <option key={v.value} value={v.value}>
                {v.label}
              </option>
            ))}
          </select>
        )}
      </div>
    </>
  )
}

export function QuickDiagnosticActionsPage() {
  const client = useQueryClient()
  const page = useQuery(quickDiagnosticActionsQuery())
  const [state, setState] = useTableState(TABLE_ID, { start: 0, length: 25, order: { column: 0, dir: 'desc' }, search: '' })
  const cols = useMemo(() => columns(), [])
  const table = useQuery({
    ...quickDiagnosticActionsTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: state.order.column,
      orderName: ORDER_NAMES[state.order.column] ?? 'id',
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState(0)

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!page.data) return <Spinner />

  const redraw = () => client.invalidateQueries({ queryKey: ['settings', 'diagnostic', 'actions'] })

  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement
    const edit = target.closest('.edit-action')
    if (edit instanceof HTMLElement) {
      setEditing(Number(edit.dataset.id ?? 0))
      return
    }
    const remove = target.closest('.remove-action')
    if (remove instanceof HTMLElement) {
      if (!window.confirm('Are you sure ?')) return
      void removeQuickDiagnosticAction(Number(remove.dataset.action ?? 0))
        .then(() => {
          toast('Action removed', 'Actions')
          void redraw()
        })
        .catch(() => toast('Something is wrong. Please try again later', 'Actions', 'error'))
    }
  }

  return (
    <div onClick={onClick}>
      <div className="row">
        <div className="col-md-6">
          <h4>Quick Diagnostic Actions</h4>
        </div>
        <div className="col-md-6 align-right">
          <button type="button" className="btn btn-default add-category-btn" onClick={() => setCreating(true)}>
            Add actions
          </button>
        </div>
      </div>
      <hr />
      <div className="row">
        <div className="col-md-12">
          <div className="panel panel-default">
            <div className="panel-heading">
              <h4>Actions</h4>
            </div>
            <div className="panel-body">
              <div className="note note-info">
                Suggest actions after <strong>Quick Diagnostic</strong> runs
              </div>
            </div>
            <DataTable
              id={TABLE_ID}
              columns={cols}
              rows={(table.data?.data ?? []) as Record<string, unknown>[]}
              total={table.data?.recordsFiltered ?? 0}
              fetching={table.isFetching}
              error={table.error ? table.error.message : null}
              onRetry={() => void table.refetch()}
              state={state}
              onState={setState}
              rowId={(row) => `row_${Number(row.id)}`}
              emptyHint="A rule suggests a command when a Quick Diagnostic reading matches its condition."
            />
          </div>
        </div>
      </div>

      <ActionModal
        id="actionModal"
        title="New action"
        open={creating}
        action={0}
        scopes={page.data.scopes}
        commands={page.data.commands}
        onClose={() => setCreating(false)}
        onSaved={() => {
          setCreating(false)
          toast('Action created', 'Actions')
          void redraw()
        }}
      />
      <ActionModal
        id="updateActionModal"
        title="Update action"
        open={editing > 0}
        action={editing}
        scopes={page.data.scopes}
        commands={page.data.commands}
        onClose={() => setEditing(0)}
        onSaved={() => {
          setEditing(0)
          toast('Action updated', 'Actions')
          void redraw()
        }}
      />
    </div>
  )
}

function ActionModal({
  id,
  title,
  open,
  action,
  scopes,
  commands,
  onClose,
  onSaved,
}: {
  id: string
  title: string
  open: boolean
  action: number
  scopes: DiagnosticScope[]
  commands: { id: number; display_name: string }[]
  onClose(): void
  onSaved(): void
}) {
  const current = useQuery({ ...quickDiagnosticActionQuery(action), enabled: open && action > 0 })
  const [form, setForm] = useState<ActionFields>(EMPTY)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) {
      setForm(EMPTY)
      return
    }
    if (current.data) {
      setForm({
        scope_id: String(current.data.scope_id),
        scope_condition: current.data.scope_condition,
        condition_value: current.data.condition_value,
        command_id: current.data.command_id > 0 ? String(current.data.command_id) : '',
      })
    }
  }, [open, current.data])

  const scope = scopes.find((s) => String(s.id) === form.scope_id)

  const pickScope = (scopeId: string) => {
    const next = scopes.find((s) => String(s.id) === scopeId)
    const c = next ? conditionsFor(next.scope_type) : { operators: ['='], values: null }
    setForm({
      ...form,
      scope_id: scopeId,
      scope_condition: c.operators[0] ?? '=',
      condition_value: c.values ? (c.values[0]?.value ?? '') : '',
    })
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    const call = action > 0 ? updateQuickDiagnosticAction(action, form) : createQuickDiagnosticAction(form)
    void call
      .then(() => {
        setBusy(false)
        onSaved()
      })
      .catch(() => {
        setBusy(false)
        toast('Something is wrong. Please try again later', 'Actions', 'error')
      })
  }

  return (
    <Modal id={id} labelledBy={`${id}Label`} open={open} onClose={onClose} title={title}>
      {action > 0 && !current.data ? (
        <Spinner />
      ) : (
        <form id={action > 0 ? 'editActionForm' : 'newActionForm'} onSubmit={submit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="control-label">Scope</label>
              <select name="scope_id" className="form-control" value={form.scope_id} onChange={(e) => pickScope(e.target.value)}>
                <option value="">Select...</option>
                {scopes.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {s.scope}
                  </option>
                ))}
              </select>
            </div>
            <ScopeConditions scope={scope} form={form} onChange={setForm} />
            {scope && (
              <div className="form-group command">
                <label>Command</label>
                <select name="command_id" className="form-control" value={form.command_id} onChange={(e) => setForm({ ...form, command_id: e.target.value })}>
                  <option value="">Select ...</option>
                  {commands.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.display_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-default" onClick={onClose}>
              Close
            </button>{' '}
            <button type="submit" className="btn btn-primary" disabled={busy || !scope}>
              Save
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}
